import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ShoppingCart, Flame, PlusCircle, MinusCircle, X, AlertTriangle, UserRound, Moon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../contexts/FirebaseContext';

// Mirrors KFCStKitts/Views/Customer/MenuItemDetailView.swift:
// hero image w/ gradient + name/category overlay, description,
// nutritional row + allergen chips (food only), price + quantity,
// Add to Cart / "Sign In to Order" (guest) / "Currently Offline".
const FOOD_RELATED_TYPES = new Set([
  'Fast Food', 'Bar & Grill', 'Pizza', 'KFC', 'BBQ Grill', 'Seafood Restaurant',
  'Cafe', 'Bakery', 'Asian', 'Indian', 'Roti/Patty', 'Roti Shop', 'Juice Stand',
  'Vegetarian', 'Snack Boxes', 'Latin', 'Mexican', 'Sushi', 'Steak House',
  'Local Restaurant'
]);

const MenuItemDetailModal = ({ item, restaurant, onClose, isOffline }) => {
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const { db } = useFirebase();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [isAppOffline, setIsAppOffline] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const isGuestMode = !currentUser;

  // iOS .task — check app offline status (skipped if parent passes isOffline)
  useEffect(() => {
    if (typeof isOffline === 'boolean') {
      setIsAppOffline(isOffline);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!db) return;
      try {
        const snap = await getDoc(doc(db, 'settings', 'global'));
        if (!cancelled) setIsAppOffline(snap.exists() ? (snap.data().isAppOffline || false) : false);
      } catch {
        if (!cancelled) setIsAppOffline(false);
      }
    })();
    return () => { cancelled = true; };
  }, [db, isOffline]);

  if (!item) return null;

  const restaurantType = restaurant?.restaurantType || item.restaurantType;
  const isFoodRelated = FOOD_RELATED_TYPES.has(restaurantType);
  const dietaryRestrictions = item.dietaryRestrictions || [];
  const allergens = item.allergens || [];

  // Optional stock tracking — when the restaurant tracks stock, the
  // quantity stepper caps at stockCount and 0 = out of stock.
  const stockTracked = item.trackStock === true;
  const stockLeft = Number.isFinite(item.stockCount) ? item.stockCount : 0;
  const outOfStock = stockTracked && stockLeft <= 0;

  // Size/variant options — retail items require a pick before adding to cart.
  const variants = Array.isArray(item.variants) ? item.variants : [];
  const variantSoldOut = (v) => v.stock != null && Number(v.stock) <= 0;
  const requiresVariant = variants.length > 0 && !selectedVariant;
  // Quantity cap: picked size's stock > item-level stock > unlimited
  const maxQuantity = selectedVariant?.stock != null
    ? Number(selectedVariant.stock)
    : (stockTracked ? stockLeft : null);

  const imageURL = item.menuItemImageURL || item.imageURL || restaurant?.bannerURL || item.restaurantBannerURL || '';

  const handleAddToCart = () => {
    // Guest mode → sign in prompt (iOS GuestLoginPromptView)
    if (isGuestMode) {
      onClose();
      navigate('/login');
      return;
    }

    if (isAppOffline) {
      window.alert("We're currently offline. Please check back later.");
      return;
    }

    // Same-restaurant cart check is handled inside CartContext.addToCart
    // (mirrors iOS clear-cart confirmation alert)
    addToCart(item, quantity, restaurant?.bannerURL || item.restaurantBannerURL, selectedVariant?.name || null);
    onClose();
  };

  const addButton = outOfStock
    ? { label: 'Out of Stock', Icon: X, cls: 'bg-gray-400' }
    : requiresVariant
      ? { label: 'Select a Size', Icon: ShoppingCart, cls: 'bg-gray-400' }
      : isGuestMode
      ? { label: 'Sign In to Order', Icon: UserRound, cls: 'bg-[#007AFF]' }
      : isAppOffline
        ? { label: 'Currently Offline', Icon: Moon, cls: 'bg-gray-400' }
        : { label: 'Add to Cart', Icon: ShoppingCart, cls: 'bg-red-600 active:bg-red-700' };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal card — grouped background like iOS systemGroupedBackground */}
      <div
        className="relative bg-[#F2F2F7] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Grabber (iOS sheet handle) */}
        <div className="w-9 h-1.5 bg-gray-300 rounded-full mx-auto mt-2 sm:hidden absolute left-1/2 -translate-x-1/2 z-20" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center"
          aria-label="Close"
        >
          <X size={16} className="text-white" />
        </button>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3 py-2">
            {/* Item image with name/category overlay */}
            <div className="relative">
              {imageURL ? (
                <img
                  src={imageURL}
                  alt={item.name}
                  className="w-full object-cover min-h-[250px] max-h-[400px]"
                  onError={(e) => {
                    const fallback = restaurant?.bannerURL || item.restaurantBannerURL;
                    if (fallback && e.target.src !== fallback) {
                      e.target.src = fallback;
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
                />
              ) : (
                <div className="w-full h-[250px] bg-gray-400/20" />
              )}

              {/* Gradient overlay — black/70 bottom → clear center */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent" />

              {/* Name + badges overlay (bottom leading) */}
              <div className="absolute bottom-0 left-0 p-4 flex flex-col items-start gap-1.5">
                <h1 className="text-[28px] font-bold text-white leading-tight drop-shadow-md">
                  {item.name}
                </h1>
                <div className="flex items-center gap-2">
                  {item.category && (
                    <span className="text-xs font-medium text-white px-2.5 py-1 bg-white/20 rounded-md">
                      {item.category}
                    </span>
                  )}
                  {isFoodRelated && dietaryRestrictions.includes('Spicy') && (
                    <span className="flex items-center gap-0.5 text-xs font-semibold text-white px-2 py-1 bg-[#FF3B30] rounded-md">
                      <Flame size={12} className="fill-white" />
                      Spicy
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Description — subheadline, secondary, 2 lines */}
            <p className="text-[15px] text-[#8E8E93] line-clamp-2 px-4">
              {item.itemDescription || item.description || `Delicious item from ${restaurant?.name || 'our menu'}`}
            </p>

            {/* Compact nutritional info — food businesses only */}
            {isFoodRelated && (item.calories > 0 || allergens.length > 0) && (
              <div className="flex items-center gap-4 px-4">
                {item.calories > 0 && (
                  <span className="flex items-center gap-1">
                    <Flame size={13} className="text-orange-500 fill-orange-500" />
                    <span className="text-[15px] font-medium text-gray-900">{item.calories} calories</span>
                  </span>
                )}
                <span className="flex-1" />
                {allergens.length > 0 && (
                  <span className="flex items-center gap-1">
                    <AlertTriangle size={13} className="text-orange-500 fill-orange-500/30" />
                    <span className="text-xs font-semibold text-orange-500">Allergen Warning</span>
                  </span>
                )}
              </div>
            )}

            {/* Allergen chips — food businesses only */}
            {isFoodRelated && allergens.length > 0 && (
              <div className="px-4 flex flex-col gap-1.5">
                <span className="text-xs text-[#8E8E93]">This item contains:</span>
                <div className="flex flex-wrap gap-1.5">
                  {allergens.map((allergen) => (
                    <span
                      key={allergen}
                      className="text-xs font-medium text-orange-500 px-2 py-1 bg-orange-500/15 rounded-md"
                    >
                      {allergen}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Price + quantity row */}
            <div className="flex items-center px-4 pt-2">
              <div className="flex flex-col gap-1">
                <span className="text-[15px] text-[#8E8E93]">Price:</span>
                <span className="text-[22px] font-bold text-gray-900">
                  ${(item.price || 0).toFixed(2)}
                </span>
              </div>

              <span className="flex-1" />

              <div className="flex items-center gap-3">
                <span className="text-[15px] text-[#8E8E93]">Quantity:</span>
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  <MinusCircle size={22} className="text-[#FF3B30]" />
                </button>
                <span className="text-[20px] font-bold text-gray-900 min-w-[30px] text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(q => maxQuantity != null ? Math.min(maxQuantity, q + 1) : q + 1)}
                  aria-label="Increase quantity"
                >
                  <PlusCircle size={22} className="text-[#34C759]" />
                </button>
              </div>
            </div>

            {/* Size picker — retail items with variants must pick one */}
            {variants.length > 0 && (
              <div className="px-4 flex flex-col gap-2">
                <span className="text-[15px] font-semibold text-gray-900">Select Size</span>
                <div className="flex flex-wrap gap-2">
                  {variants.map((v) => {
                    const soldOut = variantSoldOut(v);
                    const selected = selectedVariant?.id === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        disabled={soldOut}
                        onClick={() => {
                          setSelectedVariant(v);
                          if (v.stock != null) setQuantity(q => Math.min(q, Math.max(1, Number(v.stock))));
                        }}
                        className={`px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors ${
                          selected
                            ? 'bg-[#FF3B30] text-white'
                            : 'bg-gray-100 text-gray-900'
                        } ${soldOut ? 'opacity-40' : ''}`}
                      >
                        {v.name}{v.stock != null ? ` (${v.stock} left)` : ''}
                      </button>
                    );
                  })}
                </div>
                {!selectedVariant && (
                  <span className="text-[13px] text-orange-500">Please choose a size</span>
                )}
              </div>
            )}

            {/* Stock hint — variant stock when a size is picked, else item-level tracking */}
            {selectedVariant?.stock != null ? (
              <p className="px-4 text-[13px] font-medium text-[#8E8E93]">
                {selectedVariant.stock} in stock for {selectedVariant.name}
              </p>
            ) : stockTracked && !outOfStock && (
              <p className="px-4 text-[13px] font-medium text-[#8E8E93]">
                {stockLeft} in stock
              </p>
            )}

            {/* Add to Cart */}
            <div className="px-4 pb-4">
              <button
                onClick={handleAddToCart}
                disabled={outOfStock || requiresVariant || (isAppOffline && !isGuestMode)}
                className={`w-full flex items-center justify-center gap-2 text-white text-[17px] font-semibold py-3.5 rounded-xl transition-colors ${addButton.cls}`}
              >
                <addButton.Icon size={20} />
                {addButton.label}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuItemDetailModal;
